import { useEffect, useRef } from "react";
import SignaturePad from "signature_pad";

function finger(): boolean {
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

export function SignPad({
  value,
  color,
  onChange,
}: {
  value: string;
  color: string;
  onChange: (v: string) => void;
}) {
  const host = useRef<HTMLCanvasElement>(null);
  const pad = useRef<SignaturePad | null>(null);
  const ink = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  ink.current = value;

  useEffect(() => {
    const canvas = host.current;
    const boxEl = canvas?.parentElement;
    if (!canvas || !boxEl) return;
    const coarse = finger();
    const sp = new SignaturePad(canvas, {
      penColor: color || "#2a1b36",
      backgroundColor: "rgba(255,250,248,0.96)",
      minWidth: coarse ? 1.35 : 0.7,
      maxWidth: coarse ? 3.1 : 2.4,
    });

    function paint(restore: string) {
      const box = canvas.getBoundingClientRect();
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      const w = Math.max(1, Math.floor(box.width * dpr));
      const h = Math.max(1, Math.floor(box.height * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      canvas.getContext("2d")?.setTransform(dpr, 0, 0, dpr, 0, 0);
      sp.clear();
      if (restore.startsWith("data:image") && box.width > 2 && box.height > 2) {
        void sp.fromDataURL(restore, { width: box.width, height: box.height });
      }
    }

    paint(ink.current);
    const onEnd = () => onChangeRef.current(sp.isEmpty() ? "" : sp.toDataURL("image/png"));
    sp.addEventListener("endStroke", onEnd);
    pad.current = sp;
    const ro = new ResizeObserver(() => paint(ink.current));
    ro.observe(boxEl);
    return () => {
      ro.disconnect();
      sp.removeEventListener("endStroke", onEnd);
      sp.off();
      pad.current = null;
    };
  }, [color]);

  return (
    <div className="sg-signpad">
      <canvas ref={host} aria-label="Dessinez votre signature" />
      <button
        type="button"
        className="sg-btn ghost sg-signpad__clear"
        onClick={() => {
          pad.current?.clear();
          onChange("");
        }}
      >
        Effacer
      </button>
    </div>
  );
}
